import { pickBy, uniq, fromPairs, flatMap, omit } from 'lodash-es';
import { generatePath, match } from 'react-router';
import * as React from 'react';
import * as qs from 'query-string';

import { Search } from '@weddingspot/ws-api-client';

import { ParsedQuery } from './hooks/ApiHookCreator';
import * as SRP_URLS from '../../urls/srp-urls';
import { VENUE_PROFILE_QUOTE } from '../../urls';
import Breadcrumbs from './Breadcrumbs';
import { SRPFormikValues, SRPRouteMatchProps } from '.';
import { SRP_RESULT_PER_PAGE } from './pagination';
import { asArray } from '../../utils/arrays';
import { firstOrItem } from '../../utils/query';
import { MAX as MAX_BUDGET } from './BudgetRangeFilter';
import { MAX as MAX_GUEST } from './GuestCountFilter';
import { SRP_BASE, SRP_AFFORDABLE_BASE, SRP_RD_BASE } from '../../urls';
import { LUXURY_STYLE, LUXURY_STYLE_SRP_DEFAULT_MIN_BUDGET, SMALL_STYLE, SMALL_STYLE_SRP_DEFAULT_MAX_GUEST_COUNT } from '../../constants';

/**
 * Generate the title text to display in the meta tag of the SRP
 */

export type LocationDetailedParsed ={
    market?: string;
    region?: string;
    city?: string;
    state?: string;
    alias?: string;
    subMarket?: string;
};

export type SRPMetadata = {
    venueCount: number;
    backfillVenueCount?: number;
    page: number;
    location: Search.LocationDetailed;
    styleName?: string;
    styleUrlName?: string;
    variant?: 'affordable' | 'rd';
    maxTitleLength?: number;
    buckets?: Search.CuratedBucket[] | null;
    pageTitleOverride?: string;
    metaTitleOverride?: string;
    metaDescriptionOverride?: string;
};

export const generateMetaTitleText = (metadata: SRPMetadata) => {
    const destinationWeddings = 'Destination Weddings';
    const { venueCount, page, location, styleName, metaTitleOverride, variant} = metadata;
    const market = location.market;
    const displayLocation = location.city || location.region || location.market;
    let locationDetails = (location.city || location.region)? location.state : location.market;

    const defaultTitle = 'Wedding Venues - Price & Compare Top Venues | Wedding Spot';
    const defaultRdTitle = 'Rehearsal Dinners and Bridal Showers';
    const defaultSRPTitle = 'Wedding Venues';
    const defaultDwSRPTitle = 'Destination Wedding Venues';

    if (!!metaTitleOverride) {
        return processTemplateString(metaTitleOverride, metadata);
    }

    const isAffordable = variant === 'affordable';
    const isRd = variant === 'rd';
    const isSuperMarket = !market;
    const isDestination = market === destinationWeddings;

    const [
        CHUNK_PAGE_NUMBER,
        CHUNK_VENUES_NUM,
        CHUNK_VENUES,
        CHUNK_STYLE,
        CHUNK_AFFORDABLE,
        CHUNK_WEDDING_VENUES,
        CHUNK_LOCATION,
        CHUNK_DASH,
        CHUNK_WEDDING_SPOT,
    ] = [0, 1, 2, 3, 4, 5, 6, 7, 8];

    const titleChunks = {
        [CHUNK_PAGE_NUMBER]: page > 1 ? `Page ${page} of` : '',
        [CHUNK_VENUES_NUM]: `${venueCount}`,
        [CHUNK_VENUES]: venueCount > 1 ? ' Venues' : ' Venue',
        [CHUNK_STYLE]: styleName || '',
        [CHUNK_AFFORDABLE]: isAffordable ? 'Affordable' : '',
        [CHUNK_WEDDING_VENUES]: !isRd ? isDestination ? defaultDwSRPTitle: defaultSRPTitle : defaultRdTitle,
        [CHUNK_LOCATION]: !isSuperMarket ? `in ${[...new Set([displayLocation, locationDetails])].join(', ')}` : '', 
        [CHUNK_DASH]: '-',
        [CHUNK_WEDDING_SPOT]: '| Wedding Spot',
    };
    
    let defaultTitleTagTemplate = 
        [
            CHUNK_PAGE_NUMBER,
            CHUNK_AFFORDABLE,
            CHUNK_STYLE,
            CHUNK_WEDDING_VENUES,
            CHUNK_LOCATION,
            CHUNK_DASH,
            CHUNK_VENUES_NUM,
            CHUNK_VENUES,
            CHUNK_WEDDING_SPOT,
        ];
    
    let dwTitleTagTemplate = 
        [
            CHUNK_PAGE_NUMBER,
            CHUNK_AFFORDABLE,
            CHUNK_STYLE,
            CHUNK_WEDDING_VENUES,
            CHUNK_DASH,
            CHUNK_VENUES_NUM,
            CHUNK_VENUES,
            CHUNK_WEDDING_SPOT,
        ];

    
    const titleTagTemplate = isDestination? dwTitleTagTemplate: defaultTitleTagTemplate;

    const generatedTitleTag = titleTagTemplate
        .map((chunk) => titleChunks[chunk])
        .filter((chunkStr) => chunkStr.length > 0)
        .join(' ');

    if(generatedTitleTag)
        return generatedTitleTag;

    return defaultTitle;
};

/**
 * Get title for venues SRP depending on given flow and style
 */
const getFlowVenuesTitle = (variant?: 'affordable' | 'rd', style?: string) => {
    // todo check this when router is working
    let title = 'Wedding Venues';
    if (variant === 'rd') {
        title = 'Rehearsal Dinners and Bridal Shower Venues';
    }
    if (variant === 'affordable') {
        title = `Affordable ${title}`;
    }
    if (!!style) {
        title = `${style} ${title}`;
    }
    return title;
};

/**
 * Fetches the display location for uses in titles and other metadata
 *
 * @param metadata the search page metadta from API
 * @param locationsDirty whether or not locations have been changed in filters after pageload
 */
export const getDisplayLocation = (metadata: SRPMetadata, locationsDirty?: boolean) => {
    const { market, region, city } = metadata.location;

    const specifier = city || region;

    let displayLocation: string = `${specifier ? specifier + ', ' : ''}${market ? market : ''}`;

    if (locationsDirty) {
        if (market) {
            displayLocation = `${market}`;
        } else {
            return '';
        }
    }

    return displayLocation;
};

/**
 * Fetches the location for use in backfill, simply strips city and region out of the
 * display location logic
 *
 * @param metadata the search page metadta from API
 * @param locationsDirty whether or not locations have been changed in filters after pageload
 */
export const getBackfillLocation = (metadata: SRPMetadata, locationsDirty?: boolean) => {
    return getDisplayLocation(
        {
            ...metadata,
            location: {
                ...metadata.location,
                region: undefined,
                city: undefined,
            },
        },
        locationsDirty
    );
};

/**
 * Generate the title text to display in the h1 tag of the SRP
 *
 * @param metadata the page metadata fetch on initial page load
 * @param locationsDirty indicates whether or not the locations filter has been changed by the user
 */

type generateAllMetadataParams = { metadata: SRPMetadata; locationsChanged?: boolean; isMapMode?: boolean; search: any };
export const generateTitleText = ({ metadata, locationsChanged, isMapMode, search }: generateAllMetadataParams) => {
    // todo render h1 differently for super market pages
    // todo pass map mode when ready

    const { venueCount, location, styleName, variant, pageTitleOverride } = metadata;
    const { market, region } = location;

    const isSuperMarket = !market;
    const isStyle = !!styleName;
    const venuesTitle = getFlowVenuesTitle(variant, styleName);

    if (venueCount && isMapMode && search.bbox) {
        return 'Wedding Venues near your search area';
    }

    if (isSuperMarket) {
        return venuesTitle;
    }

    if (locationsChanged && !market) {
        return venuesTitle;
    }

    // DW is a special case because the grammar doesn't work
    if (market === 'Destination Weddings') {
        if (!locationsChanged && !!region) {
            return `${venuesTitle} in ${region}`;
        }

        return `Destination ${venuesTitle}`;
    }

    if (!!pageTitleOverride) {
        return processTemplateString(pageTitleOverride, metadata);
    }

    const displayLocation = getDisplayLocation(metadata, locationsChanged);
    return `${displayLocation ? `${displayLocation} ` : ''}${venuesTitle}`;
};

/**
 * Generate the subtitle text for the SRP
 */
export const generateSubtitleText = (metadata: SRPMetadata) => {
    const { venueCount, backfillVenueCount, page } = metadata;
    const totalPages = Math.ceil((venueCount + (backfillVenueCount || 0)) / SRP_RESULT_PER_PAGE);

    // todo pass map mode when ready
    if (!venueCount || page > totalPages) {
        return '';
    }

    let totalVenueCount = venueCount;

    if (!!backfillVenueCount) {
        totalVenueCount += backfillVenueCount;

        return `${venueCount} match${venueCount > 1 ? `es` : ``} out of ${totalVenueCount} similar venues`;
    } else {
        return `${venueCount} matching your search`;
    }
};

/**
 * Generate the description text to put to the meta description tag for the SRP
 */
export const generateMetaDescriptionText = (metadata: SRPMetadata) => {
    const { venueCount, page, location, styleName, metaDescriptionOverride, variant, maxTitleLength = 155 } = metadata;
    const market = location.market;
    const displayLocation = location.region || location.market;

    const defaultDescription =
        'Find your dream wedding venue with Wedding Spot, the only site offering instant price estimates across thousands of wedding locations.';

    if (!venueCount || !page) {
        return defaultDescription;
    }

    if (!!metaDescriptionOverride) {
        return processTemplateString(metaDescriptionOverride, metadata);
    }

    const isAffordable = variant === 'affordable';
    const isSuperMarket = !market;
    const isStyle = !!styleName;

    // Shortcut for all-venues affordable SRP
    if (isSuperMarket && !isStyle) {
        const prefix = page > 1 ? `Page ${page} of ` : undefined;
        if (isAffordable && prefix) {
            return `${prefix} affordable, budget wedding venues with Wedding Spot, the only site offering instant price estimates across thousands of wedding locations.`;
        } else if (isAffordable && !prefix) {
            return `Find your affordable, budget wedding venue with Wedding Spot, the only site offering instant price estimates across thousands of wedding locations.`;
        } else if (!isAffordable && prefix) {
            return `${prefix} wedding venues with Wedding Spot, the only site offering instant price estimates across thousands of wedding locations.`;
        } else {
            return `Find your dream wedding venue with Wedding Spot, the only site offering instant price estimates across thousands of wedding locations.`;
        }
    }

    const [
        CHUNK_DEFAULT_PREFIX,
        CHUNK_PAGE_NUMBER,
        CHUNK_AFFORDABLE,
        CHUNK_STYLE,
        CHUNK_WEDDING_VENUES,
        CHUNK_IN_LOCATION,
        CHUNK_BODY,
        CHUNK_ACROSS,
        CHUNK_VENUES_NUM,
        CHUNK_LOCATION,
        CHUNK_LOCATIONS,
    ] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    const descriptionChunks = {
        [CHUNK_DEFAULT_PREFIX]: page > 1 ? '' : 'find your dream',
        [CHUNK_PAGE_NUMBER]: page > 1 ? `page ${page} of` : '',
        [CHUNK_VENUES_NUM]: venueCount > 5 ? `${venueCount}` : '',
        [CHUNK_STYLE]: styleName || '',
        [CHUNK_AFFORDABLE]: isAffordable ? 'affordable' : '',
        [CHUNK_LOCATION]: !isSuperMarket ? `${displayLocation}` : '',
        [CHUNK_IN_LOCATION]: !isSuperMarket ? `in ${displayLocation}` : '',
        [CHUNK_WEDDING_VENUES]: 'wedding venues',
        [CHUNK_BODY]: 'with Wedding Spot, the only site offering instant price estimates',
        [CHUNK_ACROSS]: 'across',
        [CHUNK_LOCATIONS]: 'locations',
    };

    let possibleVariants = [
        [
            CHUNK_DEFAULT_PREFIX,
            CHUNK_PAGE_NUMBER,
            CHUNK_AFFORDABLE,
            CHUNK_STYLE,
            CHUNK_WEDDING_VENUES,
            CHUNK_IN_LOCATION,
            CHUNK_BODY,
            CHUNK_ACROSS,
            CHUNK_VENUES_NUM,
            CHUNK_LOCATION,
            CHUNK_LOCATIONS,
        ],
        [CHUNK_DEFAULT_PREFIX, CHUNK_PAGE_NUMBER, CHUNK_AFFORDABLE, CHUNK_STYLE, CHUNK_WEDDING_VENUES, CHUNK_IN_LOCATION, CHUNK_BODY],
    ];

    let description = defaultDescription;

    // Final assembly and text generation
    possibleVariants.forEach((variant) => {
        const curDesc =
            variant
                .map((chunk) => descriptionChunks[chunk])
                .filter((chunkStr) => chunkStr.length > 0)
                .join(' ') + '.';
        if (curDesc.length <= maxTitleLength) {
            description = curDesc;
            return false;
        }
        return;
    });

    // capitalize
    description = description.charAt(0).toUpperCase() + description.slice(1);

    return description;
};

/**
 * Generates the text to display in the backfill banner
 *
 * @param metadata the page metadata fetch on initial page load
 * @param locationsDirty indicates whether or not the locations filter has been changed by the user
 */
export const generateBackfillBannerText = (metadata?: SRPMetadata, locationsDirty?: boolean) => {
    if (!metadata) {
        return `Showing other similar venues`;
    }

    const variantDescriptor = getFlowVenuesTitle(metadata.variant);
    const backfillLocation = getBackfillLocation(metadata, locationsDirty);
    const backfillLocationContent = backfillLocation ? <> in <strong>{backfillLocation}</strong></> : '';

    return <>Showing other {variantDescriptor}{backfillLocationContent}</>;
};

/**
 * Generate the keywords to put to the meta for the SRP
 */
export const generateKeywords = (keywords?: string[]) => {
    return keywords ? keywords.join(', ') : undefined;
};

/**
 * Converts a string into URL-friendly text based on the
 * string_to_url function used in the django codebase
 */
export const nameToUrlName = (name: string | undefined | null) => {
    if (!name) {
        return '';
    }

    const pattern = /([^\s\w]|_)/g;
    return name.toLowerCase().replace('\'', '').replace(pattern, ' ').replace(/\s{2,}/g, ' ').trim().replace(/\s/g, '-');
};

/**
 * Take a flat object and convert it to URL params string (no questionmark)
 *
 * @param params A flat object
 */
export const objectToParamsStr = (params: { [key: string]: string | number | undefined | null }) => {
    return Object.entries<string | number | undefined | null>(params)
        .filter(([k, v]) => v !== undefined && v !== null)
        .map(([k, v]: [string, string | number]) => {
            return `${encodeURIComponent(k)}=${encodeURIComponent(`${v}`)}`;
        })
        .join('&');
};

/**
 * Generates the canonical URL
 * @param props.page the page number
 * @param props.market
 * @param props.region
 * @param props.style
 * @param props.zipcode
 */
export const getCanonicalUrl = (props: {
    variant?: 'rd' | 'affordable';
    page?: number;
    market?: string;
    region?: string;
    submarket?: string;
    styleUrlName?: string;
    zipcode?: string;
    city?: string;
}) => {
    const { variant, page, market, submarket, region, styleUrlName, zipcode, city } = props;

    const marketToken = nameToUrlName(market);
    const regionToken = nameToUrlName(region);
    const subMarketToken = nameToUrlName(submarket);
    const cityToken = nameToUrlName(city);
    const styleToken = styleUrlName;
    const isRd = variant === 'rd';
    const isAffordable = variant === 'affordable';
    const pageQp = !!page && page > 1 ? objectToParamsStr({ page: page }) : undefined;

    const urlPath = () => {
        // Zip code SRP
        if (zipcode) {
            return generatePath(isRd ? SRP_URLS.SRP_RD_ZIPCODE : SRP_URLS.SRP_ZIP_CODE, { zipcode: zipcode });
        }

        // Style SRPs
        if (styleToken) {
            if (market && submarket) {
                return generatePath(isRd ? SRP_URLS.SRP_RD_STYLE_SUB_MARKET : SRP_URLS.SRP_STYLE_SUB_MARKET, {
                    style: styleToken,
                    market: marketToken,
                    submarket: subMarketToken,
                });
            }
            if (market && region) {
                return generatePath(isRd ? SRP_URLS.SRP_RD_STYLE_REGION : SRP_URLS.SRP_STYLE_REGION, {
                    style: styleToken,
                    market: marketToken,
                    region: regionToken,
                });
            }
            if (market) {
                return generatePath(isRd ? SRP_URLS.SRP_RD_STYLE_MARKET : SRP_URLS.SRP_STYLE_MARKET, {
                    style: styleToken,
                    market: marketToken,
                });
            }
            return generatePath(isRd ? SRP_URLS.SRP_RD_STYLE_BASE : SRP_URLS.SRP_BASE_STYLE, { style: styleToken });
        }

        // General location SRPs
        if (market && submarket) {
            const subMarketURL = isRd ? SRP_URLS.SRP_RD_SUB_MARKET : isAffordable ? SRP_URLS.SRP_AFFRODABLE_SUB_MARKET : SRP_URLS.SRP_SUB_MARKET;

            return generatePath(subMarketURL, {
                market: marketToken,
                submarket: subMarketToken,
            });
        }
        if (market && region) {
            const regionUrl = isRd ? SRP_URLS.SRP_RD_REGION : isAffordable ? SRP_URLS.SRP_AFFRODABLE_REGION : SRP_URLS.SRP_REGION;

            return generatePath(regionUrl, {
                market: marketToken,
                region: regionToken,
            });
        }
        if (market && city) {
            const cityUrl = isRd ? SRP_URLS.SRP_RD_CITY : isAffordable ? SRP_URLS.SRP_AFFRODABLE_CITY : SRP_URLS.SRP_CITY;

            return generatePath(cityUrl, {
                market: marketToken,
                city: cityToken,
            });
        }
        if (market) {
            const marketUrl = isRd ? SRP_URLS.SRP_RD_MARKET : isAffordable ? SRP_URLS.SRP_AFFRODABLE_MARKET : SRP_URLS.SRP_MARKET;

            return generatePath(marketUrl, { market: marketToken });
        }

        // Generic all-venues SRP
        const baseUrl = isRd ? SRP_URLS.SRP_RD_BASE : isAffordable ? SRP_URLS.SRP_AFFORDABLE_BASE : SRP_URLS.SRP_BASE;

        return generatePath(baseUrl);
    };

    const path = urlPath();

    if (pageQp) {
        return `${path}?${pageQp}`;
    }

    return path;
};

export const generateContactLink = (venueId: number) => generatePath(VENUE_PROFILE_QUOTE, { id: venueId, action: 'contact' });

/**
 * Given the react-router match data, return the SRP variant
 * @param { match } match the react-router match data for the target page
 */
export const getVariantFromMatch = (match: match): 'rd' | 'affordable' | undefined => {
    const path = match.path;
    if (SRP_URLS.AFFORDABLE_SRP_URLS.indexOf(path) > -1 || SRP_URLS.AFFORDABLE_SRP_URLS.indexOf(match.url) > -1) {
        return 'affordable';
    } else if (SRP_URLS.RD_SRP_URLS.indexOf(path) > -1) {
        return 'rd';
    }
    return undefined;
};

export type MetaLinks = {
    canonical: string;
    next?: string;
    prev?: string;
};

/**
 * Returns an object containing the URLs to be used as the href attribute
 * values for the link metatags
 *
 * @param variant
 * @param parsedMetadata
 * @param venuesCount
 * @param zipcode
 */
export const getMetaLinks = (
    parsedMetadata: SRPMetadata,
    venuesCount: number,
    variant?: 'affordable' | 'rd',
    zipcode?: string
): MetaLinks => {
    const curPage = parsedMetadata.page;

    const pageCount = Math.floor(venuesCount / SRP_RESULT_PER_PAGE);
    const canonicalUrlParams = {
        variant,
        page: curPage,
        market: parsedMetadata.location.market,
        region: parsedMetadata.location.region,
        styleUrlName: parsedMetadata.styleUrlName,
        zipcode: zipcode,
        city: parsedMetadata.location.city,
        submarket: parsedMetadata.location.sub_market
    };

    const canonicalUrl = getCanonicalUrl(canonicalUrlParams);

    let prev;
    if (curPage > 1) {
        prev = getCanonicalUrl({
            ...canonicalUrlParams,
            page: curPage - 1,
        });
    }

    let next;
    if (curPage < pageCount) {
        next = getCanonicalUrl({
            ...canonicalUrlParams,
            page: curPage + 1,
        });
    }

    return {
        canonical: canonicalUrl,
        next,
        prev,
    };
};

export const generateAllMetadata = (
    metadata: Search.SearchMetadata | undefined | null,
    venues: Search.SearchResults | undefined | null,
    search: any,
    match: match<SRPRouteMatchProps>,
    locationsChanged?: boolean,
    variant?: 'rd' | 'affordable',
    isMapMode?: boolean
) => {
    const titleVariants = {
        rd: 'Rehearsal Dinner & Bridal Shower Venues',
        affordable: 'Affordable Wedding Venues',
    };
    const dataReady = venues && metadata;
    const title = variant ? titleVariants[variant] : 'Wedding Venues';
    if (!dataReady) {
        return {
            metaTitle: 'Wedding Venues &amp; Planning Tools | Wedding Spot',
            titleText: title,
            metaDescription: '',
            searchResultsText: '',
            keywords: '',
            renderedBreadcrumbs: null,
        };
    }

    const _venues = venues as Search.SearchResults;
    const _metadata = metadata as Search.SearchMetadata;

    const metadataParsed: SRPMetadata = {
        venueCount: _venues.venues_count,
        backfillVenueCount: _venues.backfill_venues_count,
        page: parseInt(search.page as string, 10) || 1,
        location: _metadata.location || {},
        styleName: _metadata.style_name,
        styleUrlName: _metadata.style_url_name?.toLowerCase(),
        buckets: _metadata.buckets,
        pageTitleOverride: _metadata.page_title_override,
        metaTitleOverride: _metadata.meta_title_override,
        metaDescriptionOverride: _metadata.meta_description_override,
        variant,
    };

    const metaTitle = generateMetaTitleText(metadataParsed);

    const titleText = generateTitleText({ metadata: metadataParsed, locationsChanged, isMapMode, search });
    // TODO strange similar name, need to rename

    const searchResultsText = generateSubtitleText(metadataParsed);

    const metaDescription = generateMetaDescriptionText(metadataParsed);

    const keywords = generateKeywords(_metadata.keywords);

    const metaLinks = getMetaLinks(metadataParsed, _venues.venues_count + _venues.backfill_venues_count, variant, match.params.zipcode);

    const isDw = metadataParsed.location.market === 'Destination Weddings';

    return {
        metaTitle,
        titleText,
        metaDescription,
        searchResultsText,
        keywords,
        renderedBreadcrumbs: <Breadcrumbs {...metadataParsed} />,
        metadataParsed,
        metaLinks,
        isDw,
    };
};

/**
 * Shared logic for converting URL to API request params
 *
 * @param match react router match object
 * @param search react router location.search object
 */
const _URLToApiParamsShared = (match: match<SRPRouteMatchProps>, search: ParsedQuery) => {
    const variant = getVariantFromMatch(match);
    const serviceCat = firstOrItem(search.sr);
    const budgetMin = firstOrItem(search.bmin);
    const budgetMax = firstOrItem(search.bmax);
    const gcMin = firstOrItem(search.gcmin);
    const gcMax = firstOrItem(search.gcmax);
    const zipcode = firstOrItem(search.z) || match.params.zipcode || null;

    const apiParamsDirty = {
        zip_code: zipcode,
        style_name: match.params.style,
        styles: asArray(search.s).map((s) => parseInt(s, 10)),
        is_rd: variant === 'rd',
        is_affordable: variant === 'affordable',
        service_category: serviceCat ? parseInt(serviceCat, 10) : undefined,

        min_budget: budgetMin ? parseInt(budgetMin, 10) : match.params.style?.toLowerCase() === LUXURY_STYLE ? LUXURY_STYLE_SRP_DEFAULT_MIN_BUDGET : null,
        max_budget: budgetMax ? parseInt(budgetMax, 10) : null,

        min_guest_count: gcMin ? parseInt(gcMin, 10) : null,
        max_guest_count: gcMax ? parseInt(gcMax, 10) : match.params.style?.toLowerCase() === SMALL_STYLE ? SMALL_STYLE_SRP_DEFAULT_MAX_GUEST_COUNT : null,
        byo: search.byo === '1',
        isdw: search.isdw === '1',

        coordinates: search.bbox,
    };

    const apiParams = pickBy(apiParamsDirty, (param) => {
        if (param == null) {
            return false;
        }
        if (Array.isArray(param) && param.length === 0) {
            return false;
        }
        if (Number.isNaN(param as number)) {
            return false;
        }
        return true;
    });

    return apiParams;
};

/**
 * Converts the current browser route and query params into API params for the search filters endpoint
 *
 * @param match react router match object
 * @param search parsed react router location search object
 */
export const URLToFilterApiParams = (match: match<SRPRouteMatchProps>, search: ParsedQuery) => {
    const { market, region, submarket } = match.params;

    let primary;
    if (search.pr) {
        primary = firstOrItem(search.pr);
    } else if (market) {
        if (market) {
            if (submarket){
                primary = `${market}:${submarket}`;
            }
            else if (region) {
                primary = `${market}:${region}`;
            } else {
                primary = market;
            }
        }
    }

    const locations = uniq(asArray(primary).concat(asArray(search.r)).concat(asArray(search.m)));

    return {
        ..._URLToApiParamsShared(match, search),
        locations,
    };
};

/**
 * Given route match props and parsed query string, return the primary location formatted
 * as market[:region]. If no primary locatin specified return null
 */
export const parsePrimaryLocation = (match: match<SRPRouteMatchProps>, search: Record<string, string | string[] | null | undefined>) => {
    if (search.pr) {
        return firstOrItem(search.pr) as string;
    }

    let { market, region } = match.params;
    if (market) {
        let location = market;
        if (region) {
            location += `:${region}`;
        }
        return location;
    }

    return null;
};

/**
 * Convert URL params to the format expected by the search API
 *
 * @param match react router match object
 * @param search react router location search object (i.e. query params from page request)
 */
export const URLToSearchApiParams = (
    match: match<SRPRouteMatchProps>,
    search: Record<string, string | string[] | null | undefined>
): Search.SearchQueryParams => {
    const commonApiParams = _URLToApiParamsShared(match, search);

    const { market, region, submarket } = match.params;

    // This logic is different because the venues search endpoint does a simple logical
    // AND with all provided location, in the case where the primary location is a market
    // and region filters are provided, the regions filters would essentially be ignored
    // due to the fact that, a logical AND of the market and region is just the market since
    // the market is a superset. To avoid this, if a primary region is specified, we only
    // include it in the search query if no other regions are specified
    let primary;
    if (search.pr) {
        if (!(search.r || search.m)) {
            primary = firstOrItem(search.pr);
        }
    } else if (market) {
        if (market) {
            if(submarket){
                primary = `${market}:${submarket}`;
            }
            else if (region) {
                primary = `${market}:${region}`;
            } else {
                primary = market;
            }
        }
    }

    const locations = uniq(asArray(primary).concat(asArray(search.r)).concat(asArray(search.m)));

    return {
        ...commonApiParams,
        locations,
        limit: SRP_RESULT_PER_PAGE,
        offset: search.page ? (parseInt(search.page as string, 10) - 1) * SRP_RESULT_PER_PAGE : 0,
    };
};

/**
 * Convers search filters endpoint API response value to SRPFormikValues format
 *
 * @param filter search fitlers API response
 */
export const ApiResponseToFormik = (filter: Search.SearchFilters | undefined | null): SRPFormikValues => {
    if (!filter) {
        // Initial formik state values before any filters data is loaded
        // we need to pre-initialize formik state in order to initialize all
        // form inputs as controlled inputs
        return {
            budget: [0, null],
            sr: 1, // TODO: Constant-ize services values
            byo: false,
            guestCount: [0, null],
            region: {},
            market: {},
            styles: {},
        };
    }
    const selectedService = filter.services?.options.find((service) => service.selected);
    const locations = filter.locations
        ? fromPairs(
              flatMap(filter.locations.options, (location) => {
                  return location.regions.map((region) => [`${location.label}:${region.label}`, region.selected]);
              })
          )
        : {};

    const selectedMarket = filter.locations
    ? fromPairs(
            flatMap(filter.locations.options, (location) => {
                return location.markets.map((market) => [`${location.label}:${market.label}`, market.selected]);
            })
        )
    : {};

    return {
        budget: filter.budget ? filter.budget.default : undefined,
        sr: selectedService ? selectedService.value : undefined,
        byo: filter.byo ? filter.byo.default : undefined,
        guestCount: filter.guests ? filter.guests.default : undefined,
        styles: filter.styles ? fromPairs(filter.styles.options.map((option) => [option.value, option.selected])) : undefined,
        region: locations,
        market: selectedMarket,
    };
};

/**
 * Converts formik props to data that can be used to replace the URL path and search
 *
 * @param values Formik values to convert
 * @param extra extra context values to augment the formik values with
 */
export const formikValuesToUrl = (
    values: SRPFormikValues,
    extra?: {
        variant?: 'rd' | 'affordable';
        search?: qs.ParsedQuery<string>;
        metaLocation?: Search.LocationDetailed | null;
    }
) => {
    const { variant = undefined, search = undefined, metaLocation = undefined } = extra || {};

    /*
    Generally we want the URL to purely be a function of the formik form
    values only, but it's necessary to carry forward some state for information
    that isn't embedded in the URL parameters like this concept of a "primary location"
    which doesn't necessarily exist as a value in the form
    */
    let primary: string | undefined;
    if (search && search.pr) {
        primary = firstOrItem(search.pr) as string;
    } else if (metaLocation && metaLocation.market) {
        primary = metaLocation.market;
    }

    let r = uniq(Object.keys(pickBy(values.region)));
    let s = Object.keys(pickBy(values.styles));
    let m = Object.keys(pickBy(values.market));
    const params = {
        r: r,
        m: m,
        pr: primary,
        s: s,
        bmin: values.budget ? values.budget[0] : null,
        // TODO currently if user select any we store in formik zero, but zero shouldn't be in URL
        bmax: values.budget ? values.budget[1] : null,

        gcmin: values.guestCount ? values.guestCount[0] : null,
        gcmax: values.guestCount ? values.guestCount[1] : null,

        sr: values.sr,
        byo: values.byo ? '1' : null,

        bbox: values.bbox,

        // This causes some strange behavior, it might be correct to never push this into the URL
        // on normalization since we already have the location params
        // z: match.params.zipcode || null,
    };

    // "Omit" is required to set the first page after each filter update.
    const notNullParams = pickBy({ ...omit(search, 'page'), ...params });

    // Clean up implicit filter values
    if (notNullParams.bmax === MAX_BUDGET) {
        delete notNullParams.bmax;
    }

    if (notNullParams.gcmax === MAX_GUEST) {
        delete notNullParams.gcmax;
    }

    let newPathName = SRP_BASE;

    // Get base URL for normalization
    const isRd = variant === 'rd';
    const isAffordable = variant === 'affordable';

    if (isAffordable) {
        newPathName = SRP_AFFORDABLE_BASE;
    }
    if (isRd) {
        newPathName = SRP_RD_BASE;
    }

    return {
        pathname: newPathName,
        search: notNullParams,
    };
};

let lastLogTime: null | number = null;
export const LogPerformance = (...args: any[]) => {
    const timeMark = lastLogTime ? Date.now() - lastLogTime : Date.now();

    // Disable LogPerformance, enable this when start profiling.
    // console.warn(timeMark.toLocaleString(), ...args);

    lastLogTime = Date.now();
};

// This function is useful when u have object/array which is similar by value,
// but U wanna verify they similar by reference/link also
const detectSameLinkCache = new Map();
export const detectSameLink = (object: any) => {
    if (!detectSameLinkCache.has(object)) {
        detectSameLinkCache.set(object, Math.random());
    }
    return { object, linkUid: detectSameLinkCache.get(object) };
};

// Sometimes u have one component with several useState,
// in this case in chrome dev tools (react extension) u will see state value, but not state name
// For simple debug I create this function, example of use
// [userName, setUserName] = useState();
// [userAge, setUserAge] = useState();
// useDebugMultiState({userName, userAge})
export const useDebugMultiState = (initialValue: any) => {
    React.useDebugValue(initialValue);
};

const processTemplateString = (template: string, metadata: SRPMetadata) => {
    return template
        .replace('%%(venue_count)%%', metadata.venueCount.toString())
        .replace('%%(venue_count_pluralize)%%', metadata.venueCount > 1 ? 's' : '');
};
